import React, { useEffect, useState } from 'react';
import { Avatar, Button, Dropdown } from 'antd';
import { useNotification } from '../../../contexts/NotificationContext/NotificationContext';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { breakpoints } from '@lib/breakpoints';
import { useRuntimeConfig } from 'vl-core/useRuntimeConfig';
import useAvatar from 'vl-common/src/hooks/useAvatar';
import { TestPatientSimpleVideo } from '@components/Video/SimpleVideo';
import { useStopTwilioOnUnmount } from '@hooks/useStopTwilioOnUnmount';
import useUser from '@vl-core/hooks/useUser';
import { MdVideocam } from 'react-icons/md';
import { FaPhoneAlt } from 'react-icons/fa';
import { DownOutlined, InfoCircleOutlined, SendOutlined } from 'antd/icons';
import { useTestPatientRoom } from '@hooks/useTestPatientRoom';
import useTestPatientCallStatus from '@hooks/useTestPatientCallStatus';
import useAudio from '@hooks/useAudio';
import type { RemoteParticipant } from 'twilio-video';
import { CallManager } from '@helpers/CallManager';
import { BsFillRecord2Fill } from 'react-icons/bs';
import { SetTestCallStepType, TestCallStep } from './ClinicianCallTestSession';
import {
  PatientStatusPanelStyled,
  ExpandedContent,
  StyledPatientAvatar,
  StyledPatientName
} from '../Session/PatientStatusPanel';

const MAX_WIDTH = breakpoints.md;
const MIN_WIDTH = breakpoints.md + 1;

const StyledAppointmentStatus = styled.div`
  font-weight: 500;
  font-size: 18px;
  color: var(--productGray);
  text-align: center;
`;

const StyledAppointmentContext = styled.div`
  font-size: 14px;
`;

type TestType = React.Dispatch<React.SetStateAction<{ video: boolean; audio: boolean }>>;

type CallMenu = {
  signalingManager: CallManager | undefined;
  canSelect: boolean;
  setTestType: TestType;
  setShowRinger: (value: boolean) => void;
  hasParticipants: boolean;
};

type RingerButtons = {
  setShowRinger: (value: boolean) => void;
  signalingManager: CallManager | undefined;
  setTestType: TestType;
};

type CallTestPatientStatusPanel = {
  setTestCallSignalingManager: (value: CallManager) => void;
  setTestCallStep: SetTestCallStepType;
  testCallStep: TestCallStep;
  callCompleted: boolean;
  setCallCompleted: (value: boolean) => void;
  isBlockedByMedia: boolean;
};

const CallMenu = ({ signalingManager, canSelect, setTestType, setShowRinger, hasParticipants }: CallMenu) => {
  const { t } = useTranslation('common');

  return (
    <Dropdown
      menu={{
        items: [
          {
            key: 'video',
            icon: <MdVideocam style={{ color: canSelect ? 'var(--productPrimary)' : 'rgba(0, 0, 0, 0.25)' }} />,
            label: 'Video call',
            disabled: !canSelect || !hasParticipants,
            onClick: () => {
              if (!signalingManager) {
                return;
              }
              if (!hasParticipants) {
                return;
              }
              setShowRinger(hasParticipants);

              // The auto patient connects in the background regardless if
              // the video call has started or not. This means the actual
              // callStatus will show as 'in-progress'.
              // The auto patient will instantly connect. So the 3 second
              // delay is specified here emulating the wait for the connection.
              setTimeout(() => {
                setShowRinger(false);
                setTestType((test) => ({ ...test, video: true }));
                signalingManager.offerToStart();
              }, 3000);
            }
          },
          {
            key: 'primary',
            icon: <FaPhoneAlt style={{ color: 'rgba(0, 0, 0, 0.25)' }} />,
            label: 'Call Primary Phone Number - not implemented yet',
            disabled: true,
            onClick: () => {}
          },
          {
            key: 'one-time-link',
            label: 'Send one-time video link',
            icon: <SendOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />,
            disabled: true,
            onClick: () => {}
          }
        ]
      }}
      placement="bottom"
      trigger={['click']}
    >
      <Button
        type={'primary'}
        data-testid="call-patient"
        css={css`
          margin-top: 10px;
        `}
      >
        {t('Test Call')} <DownOutlined />
      </Button>
    </Dropdown>
  );
};

const RingerButtons = ({ setShowRinger, signalingManager, setTestType }: RingerButtons) => {
  const { t } = useTranslation('common');

  return (
    <>
      <div
        css={css`
          display: flex;
          width: 100%;
          justify-content: center;

          .calling-alert {
            width: 50%;
            padding: 10px;
            border-radius: 10px;

            &.calling {
              background-color: grey;
              text-align: center;
            }

            @media (max-width: 768px) {
              width: 90%;
            }
          }

          .calling-alert-text {
            font-size: 24px;
            font-weight: 500;
            margin-bottom: 0;
            color: white;
            @media (max-width: 768px) {
              font-size: 18px;
            }
          }
        `}
      >
        <div className="calling-alert calling">
          <span className="calling-alert-text">{t('Calling Patient...')}</span>
        </div>
      </div>

      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
        `}
      >
        <Button
          size="large"
          data-testid="cancel-test-button"
          onClick={() => {
            setShowRinger(false);
            setTestType((test) => ({ ...test, video: false }));
            if (signalingManager) {
              signalingManager.cancelCall();
              signalingManager.disconnect();
            }
          }}
          css={css`
            width: 50%;
            border-radius: 10px;
            color: red;
            background-color: white;
            border: 2px solid red;
          `}
        >
          {t('Stop calling')}
        </Button>
      </div>
    </>
  );
};

const RecordingIndicators = ({ stopRecording }: { stopRecording: () => void }) => {
  const { t } = useTranslation('common');
  const [timeLeft, setTimeLeft] = useState(30);

  useEffect(() => {
    const countdownTimeout = setTimeout(() => {
      const countdownInterval = setInterval(() => {
        setTimeLeft((time) => {
          if (time <= 1) {
            clearInterval(countdownInterval);
            stopRecording();
            return 0;
          }
          return time - 1;
        });
      }, 1000);
      return () => clearInterval(countdownInterval);
    }, 0);
    return () => clearTimeout(countdownTimeout);
  }, []);

  return (
    <div
      css={css`
        width: 25vw;
        position: relative;
        top: 60px;
        display: flex;
        justify-content: space-between;

        .indicators {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          border-radius: 5px;
          padding: 5px 10px;
          color: #fff;
        }

        .recording {
          background-color: #ff0000;
        }

        .countdown {
          background-color: #000;
        }

        .countdown-value {
          display: flex;
          justify-content: center;
          width: 20px;
          margin-left: 2px;
        }
      `}
    >
      <div className="indicators recording">
        <BsFillRecord2Fill />
        {t('Recording')}
      </div>
      <div className="indicators countdown">
        <div className="countdown-value">{timeLeft}s</div>
        {t('remaining')}
      </div>
    </div>
  );
};

const CallTestPatientStatusPanel = ({
  setTestCallSignalingManager,
  setTestCallStep,
  testCallStep,
  callCompleted,
  setCallCompleted,
  isBlockedByMedia
}: CallTestPatientStatusPanel) => {
  const { PVIDEO } = useRuntimeConfig();
  const { t } = useTranslation('common');
  const { errorNotification } = useNotification();
  const [testType, setTestType] = useState({ video: false, audio: false });
  const user = useUser();

  const [showRinger, setShowRinger] = useState(false);
  const avatar = useAvatar(user.user_guid);
  const [, playVideoRinger, stopVideoRinger] = useAudio('/static/telephone-ring.mp3', 0.6);

  const { room, isReady, signalingManager, error: connectionError } = useTestPatientRoom(user.user_guid);
  const callStatus = useTestPatientCallStatus(room, callCompleted);
  const [participantCount, setParticipantCount] = useState(0);

  /* We store the signalingManager to use in the
  instructions panel. Calling the useTestPatientRoom hook
  in the component returns signalingManager as undefined
  */
  useEffect(() => {
    if (signalingManager) {
      setTestCallSignalingManager(signalingManager);
    }
  }, [signalingManager]);

  if (connectionError) {
    errorNotification({
      message: 'Connection Error',
      description: connectionError.toString()
    });
  }

  useStopTwilioOnUnmount(room);

  useEffect(() => {
    if (!room) return;

    setParticipantCount(room.participants.size);

    const handleConnected = (_participant: RemoteParticipant) => {
      setParticipantCount(room.participants.size);
    };
    const handleDisconnected = (_participant: RemoteParticipant) => {
      setParticipantCount(room.participants.size);
    };

    room.on('participantConnected', handleConnected);
    room.on('participantDisconnected', handleDisconnected);

    return () => {
      room.off('participantConnected', handleConnected);
      room.off('participantDisconnected', handleDisconnected);
    };
  }, [room]);

  useEffect(() => {
    if (showRinger) {
      playVideoRinger();
      const cancelCallTimeout = setTimeout(() => {
        signalingManager?.cancelCall();
        signalingManager?.disconnect();
        setShowRinger(false);
        setTestType((test) => ({ ...test, video: false }));
        stopVideoRinger();
      }, 30000);
      return () => {
        clearTimeout(cancelCallTimeout);
      };
    } else {
      stopVideoRinger();
    }
    return () => {};
  }, [showRinger]);

  return (
    <PatientStatusPanelStyled>
      <div
        css={css`
          @media (max-width: ${MAX_WIDTH}px) {
            height: 70vh;
          }

          @media (min-width: ${MIN_WIDTH}px) {
            min-width: 30vw;
            width: 30vw;
          }
        `}
      >
        <ExpandedContent>
          {testType.video && callStatus === 'in-progress' ? (
            <>
              {testCallStep === 'recording' && (
                <RecordingIndicators
                  stopRecording={() => {
                    if (!signalingManager) {
                      errorNotification({
                        message: 'Connection Error',
                        description: 'Unable to stop recording. Please try again or refresh the page.'
                      });
                      return;
                    }
                    signalingManager.stopRecording();
                  }}
                />
              )}
              {PVIDEO === 'twilio' && (
                <TestPatientSimpleVideo
                  room={room!}
                  otherPartyName={t('Test Patient')}
                  onRequestHangup={() => {
                    setCallCompleted(true);
                    setTestCallStep('complete');
                  }}
                />
              )}
            </>
          ) : (
            <>
              <StyledPatientAvatar>
                <Avatar src={avatar} />
              </StyledPatientAvatar>
              <StyledPatientName>{t('Test Patient')}</StyledPatientName>
              {!callCompleted && (
                <>
                  <StyledAppointmentStatus>{t('Your appointment is due now')}</StyledAppointmentStatus>
                  {showRinger && (
                    <RingerButtons
                      setShowRinger={setShowRinger}
                      signalingManager={signalingManager}
                      setTestType={setTestType}
                      data-testid="call-ringer-buttons"
                    />
                  )}
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      gap: 14px;
                    `}
                  >
                    <StyledAppointmentContext>
                      {t('Patient status')}: {participantCount ? t('Online') : t('Offline')}
                    </StyledAppointmentContext>
                    <StyledAppointmentContext>{t('Call status: not-started')}</StyledAppointmentContext>
                  </div>
                  <span
                    css={css`
                      text-align: center;
                    `}
                  >
                    <InfoCircleOutlined /> {t('You can use this at any time to test calling')}
                  </span>

                  <CallMenu
                    canSelect={isReady && !isBlockedByMedia}
                    signalingManager={signalingManager}
                    setTestType={setTestType}
                    setShowRinger={setShowRinger}
                    hasParticipants={participantCount > 0}
                  />
                </>
              )}
              {callCompleted && (
                <>
                  <p>{t('Test call complete')}</p>
                  <Button
                    data-testid="refresh-btn"
                    onClick={() => {
                      globalThis.window.location.reload();
                    }}
                  >
                    {t('Retry')}
                  </Button>
                </>
              )}
            </>
          )}
        </ExpandedContent>
      </div>
    </PatientStatusPanelStyled>
  );
};

export default CallTestPatientStatusPanel;
